import { useCallback } from "react";
import {
  Banner,
  Button,
  Flex,
  Grid as DSGrid,
  LoadingIndicator,
  NumberedPagination,
  Select,
  Text,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  DEFAULT_STATUSES,
  PAGE_SIZE_OPTIONS,
  type DdqQueuePageSize,
  type UseDdqQueueFiltersResult,
} from "../useDdqQueueFilters";
import { columnDefs } from "./columnDefs";
import { getDdqQueueRowId, getSortFromColumnState } from "./columnHelpers";
import * as styles from "./styles_vanilla.css";
import type { DdqQueueRow } from "../types";
import type { UseDdqProdOpsQueueResult } from "../useDdqProdOpsQueue";
import type {
  FilterChangedEvent,
  GridReadyEvent,
  RowClickedEvent,
  SortChangedEvent,
} from "ag-grid-community";
import type {
  DdqProdOpsWorkflowStatus,
  DdqProdOpsWorkflowType,
} from "~/graphql/supergraphEnums";

const gridOptions = {
  suppressCellFocus: true,
  rowHeight: 48,
  getRowId: getDdqQueueRowId,
  suppressContextMenu: true,
  // The backend only sorts by one field at a time; getSortFromColumnState would otherwise pick
  // the wrong one of two shift-click-sorted columns.
  suppressMultiSort: true,
};

export type GridProps = {
  queue: UseDdqProdOpsQueueResult;
  onRowClicked: (row: DdqQueueRow) => void;
  filters: UseDdqQueueFiltersResult;
};

export const Grid = ({ queue, onRowClicked, filters }: GridProps) => {
  const { rows, totalCount, isLoading, error, refetch } = queue;
  const {
    pageSize,
    offset,
    setPageSize,
    setOffset,
    setColumnWorkflowTypes,
    setColumnStatuses,
    setSort,
  } = filters;

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<DdqQueueRow>) => {
      if (event.data) onRowClicked(event.data);
    },
    [onRowClicked],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<DdqQueueRow>) => {
      const model = event.api.getFilterModel() as Record<
        string,
        { values?: unknown[] } | undefined
      >;
      const workflowTypeValues = model.workflowType?.values;
      const statusValues = model.status?.values;
      setColumnWorkflowTypes(
        workflowTypeValues ? (workflowTypeValues as DdqProdOpsWorkflowType[]) : null,
      );
      setColumnStatuses(statusValues ? (statusValues as DdqProdOpsWorkflowStatus[]) : null);
    },
    [setColumnWorkflowTypes, setColumnStatuses],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<DdqQueueRow>) => {
      const sort = getSortFromColumnState(event);
      setSort(sort?.sortBy ?? DEFAULT_SORT_BY, sort?.sortOrder ?? DEFAULT_SORT_ORDER);
    },
    [setSort],
  );

  // ag-grid's Set Filter reads "every value checked" as inactive, so without seeding this,
  // unchecking just one unrelated status leaves Completed checked and leaks it into the query.
  const handleGridReady = useCallback((event: GridReadyEvent<DdqQueueRow>) => {
    event.api.setFilterModel({ status: { filterType: "set", values: DEFAULT_STATUSES } });
  }, []);

  const handlePageSizeChange = useCallback(
    (value: string | number | string[] | number[]) => {
      setPageSize(Number(value) as DdqQueuePageSize);
    },
    [setPageSize],
  );

  const handlePageClick = useCallback(
    (page: number | null) => {
      if (page) setOffset((page - 1) * pageSize);
    },
    [pageSize, setOffset],
  );

  if (isLoading && rows.length === 0) {
    return (
      <Flex
        className={styles.gridWrapper}
        justifyContent="center"
        alignItems="center"
        data-testid="ddq-prod-ops-queue-grid-loading"
      >
        <LoadingIndicator size="medium" />
      </Flex>
    );
  }

  // Only `assignee` is nullable in the selection, so it's the only field that can fail
  // without null-propagating the whole response away.
  if (error && rows.length === 0) {
    return (
      <Flex
        className={styles.gridWrapper}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="small"
        data-testid="ddq-prod-ops-queue-grid-error"
      >
        <Text variant="regular-md">Couldn&apos;t load the DDQ queue.</Text>
        <Text variant="regular-sm" color={Colors.Neutral700} align="center">
          {error.message}
        </Text>
        <Button variant="neutral" onClick={refetch}>
          Retry
        </Button>
      </Flex>
    );
  }

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const activePage = Math.floor(offset / pageSize) + 1;
  const rangeStart = totalCount === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + rows.length, totalCount);

  return (
    <Flex className={styles.gridWrapper} flexDirection="column">
      {error && (
        <Banner
          variant="warning"
          title="Some assignees couldn't be loaded."
          data-testid="ddq-prod-ops-queue-grid-partial-error"
        />
      )}
      <div className={styles.gridArea} data-testid="ddq-prod-ops-queue-grid">
        <DSGrid
          columnDefs={columnDefs}
          rowData={rows}
          gridOptions={gridOptions}
          onRowClicked={handleRowClicked}
          onGridReady={handleGridReady}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
        />
      </div>
      {totalCount > 0 && (
        <Flex
          className={styles.paginationBar}
          justifyContent="space-between"
          alignItems="center"
          data-testid="ddq-prod-ops-queue-pagination"
        >
          <Text variant="regular-sm" color={Colors.Neutral700}>
            Showing {rangeStart}-{rangeEnd} of {totalCount} requests
          </Text>
          <Flex alignItems="center" gap="small">
            <Select
              label="Rows per page"
              hideLabel
              value={String(pageSize)}
              onChange={handlePageSizeChange}
              // Opening down clips the dropdown against the table container's overflow:hidden.
              position="up"
              data-testid="ddq-prod-ops-queue-page-size"
            >
              <Select.Section>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <Select.Option key={size} value={String(size)}>
                    {size} / page
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
            <div data-testid="ddq-prod-ops-queue-pagination-nav">
              <NumberedPagination
                total={totalPages}
                activePage={activePage}
                handleClick={handlePageClick}
              />
            </div>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
